/**
 * @file shop.ts
 *
 * Shop-level reads the catalog write path depends on. Today that is the shop
 * currency: the commerce-engine validates that every `Money` on a product matches
 * the shop's single configured currency, so {@link getShopCurrency} fetches it from
 * `GET /v1/shops/settings` and caches it for the session (it rarely changes). The
 * cache keeps every create/update from re-fetching the settings; {@link resetShopCurrencyCache}
 * clears it for tests.
 */

import { v1Get } from './v1-request';
import type { ShopSettingsResponseV1 } from './v1-types';

let cachedCurrency: string | null = null;

/**
 * Returns the shop's ISO-4217 currency code, fetching it once and caching it.
 *
 * @returns The shop currency (e.g. `"EUR"`).
 *
 * @throws {WcApiError} When the settings request fails.
 */
export async function getShopCurrency(): Promise<string> {
  if (cachedCurrency !== null) {
    return cachedCurrency;
  }
  const res = await v1Get<ShopSettingsResponseV1>('/shops/settings');
  cachedCurrency = res.data.general.currency;
  return cachedCurrency;
}

/** Clears the cached shop currency (for tests). */
export function resetShopCurrencyCache(): void {
  cachedCurrency = null;
}
